/**
 * The product page: stepping through the photos, choosing a variant, and the amount with the add
 * button, which adds to the cart without leaving the page. The volume price rows, the amount and
 * the price follow each other. Without this script the first photo shows and the form posts to
 * the cart.
 */
import { addToCart } from '@theme/cart-api';

class ProductGallery extends HTMLElement {
  #controller = new AbortController();
  #index = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #photos() {
    return [...this.querySelectorAll('.sda-gallery > img')];
  }

  #onClick = (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    const step = event.target.closest('[data-gallery-step]');
    if (thumb) this.#show(Number(thumb.dataset.galleryThumb));
    else if (step) this.#show(this.#index + Number(step.dataset.galleryStep));
  };

  #show(index) {
    const photos = this.#photos;
    this.#index = Math.max(0, Math.min(index, photos.length - 1));

    photos.forEach((photo, i) => photo.toggleAttribute('hidden', i !== this.#index));
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (Number(thumb.dataset.galleryThumb) === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
    this.querySelector('[data-gallery-current]').textContent = this.#index + 1;
    this.querySelector('[data-gallery-step="-1"]').disabled = this.#index === 0;
    this.querySelector('[data-gallery-step="1"]').disabled = this.#index === photos.length - 1;
  }
}

class ProductForm extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.addEventListener('click', this.#onStep, { signal });
    this.querySelector('form').addEventListener('submit', this.#onSubmit, { signal });
    this.#input.addEventListener('change', () => this.#setQuantity(this.#quantity), { signal });
    this.#input.addEventListener('input', () => this.#markTier(), { signal });
    this.#input.addEventListener('focus', () => this.#input.select(), { signal });
    this.querySelector('[data-tiers]')?.addEventListener('change', this.#onTier, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #input() {
    return this.querySelector('input[name="quantity"]');
  }

  get #quantity() {
    return Math.max(1, Number.parseInt(this.#input.value, 10) || 1);
  }

  #onStep = (event) => {
    const step = Number(event.target.closest('[data-step]')?.dataset.step);
    if (step) this.#setQuantity(this.#quantity + step);
  };

  // A volume price row sets the amount; the amount marks the highest row it reaches.
  #onTier = ({ target }) => {
    this.#setQuantity(Number(target.value));
  };

  #setQuantity(quantity) {
    this.#input.value = Math.max(1, quantity);
    this.#markTier();
  }

  #markTier() {
    const tiers = [...this.querySelectorAll('[data-tiers] input')];
    if (!tiers.length) return;
    const quantity = this.#quantity;
    const reached = tiers.findLast((tier) => Number(tier.value) <= quantity);
    const next = tiers.find((tier) => Number(tier.value) > quantity);
    reached.checked = true;
    this.#showTierPrice(reached);

    const nudge = this.querySelector('[data-nudge]');
    const { nudge: toNext, save, moneyFormat } = nudge.dataset;
    nudge.querySelector('[data-nudge-text]').textContent = next
      ? toNext.replace('[count]', Number(next.value) - quantity).replace('[price]', next.dataset.price)
      : save.replace('[amount]', formatMoney(Number(reached.dataset.saving) * quantity, moneyFormat));
  }

  // The price under the title shows the price per unit of the row reached, with today's price crossed out.
  #original;
  #showTierPrice(tier) {
    const info = this.closest('.sda-pdp__info');
    const price = info?.querySelector('.sda-pdp__price .sda-price');
    if (!price) return;
    const unit = info.querySelector('.sda-pdp__unit');
    this.#original ??= { price: price.cloneNode(true), unit: unit?.textContent };

    if (!tier.dataset.price) {
      price.replaceWith(this.#original.price.cloneNode(true));
      if (unit) unit.textContent = this.#original.unit;
      return;
    }

    const today = this.#original.price.querySelector('.sda-price__now').textContent.trim();
    const was = Object.assign(document.createElement('span'), { className: 'sda-vh', textContent: `${tier.closest('[data-tiers]').dataset.was} ` });
    const old = Object.assign(document.createElement('s'), { className: 'sda-price__old' });
    old.append(was, today);
    const now = Object.assign(document.createElement('span'), { className: 'sda-price__now', textContent: tier.dataset.price });
    price.replaceChildren(now, ' ', old);
    price.classList.add('sda-price--sale');
    if (unit && tier.dataset.unit) unit.textContent = tier.dataset.unit;
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    const button = this.querySelector('button[type="submit"]');
    const error = this.querySelector('[data-error]');
    if (button.disabled) return;

    button.disabled = true;
    error.textContent = '';
    try {
      await addToCart(this.dataset.variantId, this.#quantity);
      const { productId, productUrl, productTitle } = this.dataset;
      document.dispatchEvent(
        new CustomEvent('cart:added', { detail: { productId, productUrl, productTitle, source: this } })
      );
    } catch (failure) {
      error.textContent = failure.message;
    } finally {
      button.disabled = false;
    }
  };
}

/**
 * The pills that choose a variant. The section and the buy bar are rendered
 * again by Shopify for the chosen variant and swapped in, and the address
 * keeps the choice so the page can be shared and reloaded.
 */
class VariantPicker extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.addEventListener('change', this.#onChange, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #onChange = async ({ target }) => {
    const info = this.closest('.sda-pdp__info');
    const ids = [info, document.querySelector('[data-buy-bar]')]
      .map((part) => part?.closest('.shopify-section')?.id.replace('shopify-section-', ''))
      .filter(Boolean);
    const url = new URL(location.href);
    url.searchParams.set('variant', target.dataset.variantId);

    info.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${url}&sections=${ids.join(',')}`);
      if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
      const sections = await response.json();

      for (const html of Object.values(sections)) {
        const fresh = new DOMParser().parseFromString(html ?? '', 'text/html');
        for (const selector of ['.sda-pdp__info', '[data-buy-bar]']) {
          const part = fresh.querySelector(selector);
          if (part) document.querySelector(selector)?.replaceWith(part);
        }
      }
      history.replaceState(history.state, '', url);
      document.querySelector(`variant-picker input[name="${target.name}"]:checked`)?.focus();
      watchBuyRow();
    } catch {
      location.assign(url);
    }
  };
}

/**
 * Formats cents with the shop's money format, such as `€{{amount_with_comma_separator}}`.
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const [whole, decimals] = amount.split('.');
  const dotted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const placeholders = {
    amount,
    amount_no_decimals: Math.round(cents / 100).toString(),
    amount_with_comma_separator: `${dotted},${decimals}`,
    amount_no_decimals_with_comma_separator: dotted,
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => placeholders[key] ?? match);
}

// From 990px the buy bar only shows while the page's own add button is out of view.
let buyRowObserver;
function watchBuyRow() {
  const buyBar = document.querySelector('[data-buy-bar]');
  const buyRow = document.querySelector('product-form');
  buyRowObserver?.disconnect();
  if (!buyBar || !buyRow) return;

  buyBar.classList.add('is-idle');
  buyRowObserver = new IntersectionObserver(([entry]) => buyBar.classList.toggle('is-idle', entry.isIntersecting));
  buyRowObserver.observe(buyRow);
}
watchBuyRow();

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
